import { SINGLE_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getRootContainerProps } from "@client/content/lib/helper";
import { makeButton } from "@client/content/modules/Button";
import { type ButtonContainerProps } from "@client/content/modules/Button/container";
import { type RootContainerProps } from "@client/content/types";
import Icon from "@client/controls/Icon/component";
import Timeline, { type TimelineItemInfo } from "@client/controls/Timeline/component";
import { TargetSelector } from "@client/selectors";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { i18nFormat } from "@common/lib/i18n";
import classNames from "classnames";
import { isNull } from "lodash";
import React from "react";
import { type BlogDetailComments } from "../types";
import CommentForm from "./CommentForm/container";

export type MapProps = RootContainerProps & {
  comments: BlogDetailComments.PostComment[];
  commentCount: number;
  hasMore: boolean;
  status: BlogDetailComments.Status;
};

export type ActionProps = {
  loadMore: () => void;
};

const MoreButton = makeButton<ButtonContainerProps>(TargetSelector.Entity, "moreButton");
export default (props: MapProps & ActionProps) => {
  const i18n = useI18n();

  const timelineComments = React.useMemo<TimelineItemInfo[]>(
    () =>
      props.comments.map<TimelineItemInfo>((x) => ({
        color: x.authorProfilePictureUrl ? undefined : SystemColor.Gray,
        date: x.createdOnDate,
        title: x.authorName,
        icon: x.authorProfilePictureUrl ? <img src={x.authorProfilePictureUrl} /> : IconType.User,
        content: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: x.comment.replace(/\n/gm, "<br/>") }} />

            {x.isApproved ? null : (
              <span
                className={classNames("status", { awaiting: isNull(x.isApproved), declined: !isNull(x.isApproved) })}
              >
                <Icon iconClassName={isNull(x.isApproved) ? IconType.Pause : IconType.CrossCircle} />
                {isNull(x.isApproved) ? i18n.awaitingModeration : i18n.declined}
              </span>
            )}
          </div>
        )
      })),
    [props.comments]
  );

  return (
    <div>
      <CommentForm {...getRootContainerProps(props)} />

      <div className="comments">
        <strong>{i18nFormat(i18n.commentsN, ["N", props.commentCount])}</strong>
        <Timeline hasLine={false} items={timelineComments} dontSort />

        {props.hasMore && (
          <div style={SINGLE_BUTTONS_STYLE}>
            <MoreButton
              {...getRootContainerProps(props)}
              isDisabled={props.status === "LoadingMore"}
              onClick={props.loadMore}
            />
          </div>
        )}
      </div>
    </div>
  );
};
